import fetch from 'isomorphic-fetch'

import Toast from 'components/Toast'

import { setSuccess, setFetchFail } from 'api/remoteData'
import store, { Auth, Response } from 'store'
import { UnknownRecord } from 'types/global'
import { clearLogout } from 'services/auth'

export * from './response'
import { ERROR_CODES, ERROR_MESSAGES } from './response'

type Options = {
  requireAuthorization?: boolean
  headers?: UnknownRecord
  reloadOnReauth?: boolean
}

// Prompt feedback once, and always redirect to login
const processExpiredAuth = (options?: Options) => {
  const isTokenExpired = store.getState().auth.authExpired.isTokenExpired

  if (isTokenExpired) return

  Toast.toastMessage({
    message: 'session_expired_description_msg',
    variant: 'error',
  })

  clearLogout(store.dispatch)

  store.dispatch(
    Auth.setAuthExpired({
      isTokenExpired: true,
      // Save current path
      lastPathname: window.location.pathname !== '/auth/login' ? window.location.pathname : null,
      //TODO: Save params here later, keep it null for now
      lastPathState: null,
      // Reload page after successful re-login by default
      reloadOnReauth: options?.hasOwnProperty('reloadOnReauth') ? options?.reloadOnReauth : true,
    }),
  )
}

const callAPI = async <D extends UnknownRecord, P extends UnknownRecord = UnknownRecord>(
  method: string,
  params?: P | Array<P>,
  options?: Options,
) => {
  const currentToken = store.getState().auth.token || localStorage.getItem('token')
  const currentBranchId = store.getState().user.selectedBranch?.id ?? ''

  const result = fetch(process.env.API_URL || '/api', {
    method: 'POST',
    headers: {
      'App-ID': process.env.APP_URL ?? location.hostname,
      'App-Version': process.env.REACT_APP_VERSION || '-',
      'App-Locale': localStorage.getItem('LANGUAGE_SELECTION') || '',
      'Client-Timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
      'Content-Type': 'application/json',
      'Login-Source-ID': '1',
      ...options?.headers,
      ...(options?.requireAuthorization && {
        Authorization: `Bearer ${currentToken}`,
        'Branch-ID': currentBranchId,
      }),
    },
    body: JSON.stringify({
      crm_client_version: process.env.REACT_APP_VERSION,
      jsonrpc: '2.0',
      id: 1,
      method,
      params,
    }),
  })

  return result
    ?.then((res: Response) => {
      if (!res.ok) {
        return setFetchFail({
          code: res.status,
          message: res.statusText,
          name: res.type,
        })
      }
      return res
        .json()
        .then((json: ResponseType<D>) => {
          if (json.error) {
            store.dispatch(Response.setError(json.error))

            // This is to auto expire token whenever we change the JWT secret key.
            if (
              json.error.code === ERROR_CODES.INVALID_REQUEST &&
              json.error.message === ERROR_MESSAGES.SIGNATURE_VERIFICATION_FAILED
            ) {
              processExpiredAuth()
            }

            // Detect an expired token response
            if (json.error.code === ERROR_CODES.TOKEN_IS_EXPIRED) {
              processExpiredAuth()
            }

            return setFetchFail(json.error)
          }

          // Check refresh token
          // Do not update token if the request is crm_auth_logout
          if (json.auth_token && method !== 'crm_auth_logout') {
            store.dispatch(Auth.updateAuth(json.auth_token))
          }

          store.dispatch(Response.reset())
          return setSuccess(json.result)
        })
        .catch((err: Error) =>
          setFetchFail({
            message: err.message,
            name: 'INVALID_JSON',
            code: -2,
          }),
        )
    })
    .catch((err: Error) =>
      setFetchFail({
        name: err.name,
        message: err.message === 'Failed to fetch' ? 'Check your network connection.' : err.message,
        code: -1,
      }),
    )
}

export default callAPI

export type ResponseType<D extends UnknownRecord> = {
  auth_token?: string
  id: number
  jsonrpc: string
  result: D
  error?: {
    code: number
    message: string
    name: string
  }
}
